'use client'

import { Heart, LogOut, MapPin, Medal, PackageSearch, Pencil, Settings, ShieldCheck } from 'lucide-react'
import Skeleton from '@/components/elements/Skeleton'
import { formatImageURL, isLoyaltyActive } from '@/utils/util'
import Avatar from 'boring-avatars'
import Link from 'next/link'
import { useProfileNav, type ProfileNavKey } from '@/lib/commerce/hooks'
import { getVendorPrimaryColor, resolveColorHex } from '@/utils/util'

const ICONS: Record<ProfileNavKey, any> = {
  orders: PackageSearch,
  loyalty: Medal,
  wishlist: Heart,
  addresses: MapPin,
  settings: Settings,
  logout: LogOut,
}

export default function ProfileCard({ customer, vendor, isLoading = false, setIsLoading, onLinkClick }: { customer: any, vendor: any, isLoading?: boolean, setIsLoading?: (isLoading: boolean) => void, onLinkClick?: () => void }) {
  const { accountItems, logoutItem } = useProfileNav()
  const visibleItems = accountItems.filter(item => item.key !== 'loyalty' || isLoyaltyActive(vendor))
  const primaryHex = resolveColorHex(getVendorPrimaryColor(vendor))
  const brandGradient = `linear-gradient(135deg, ${primaryHex} 0%, ${primaryHex}99 100%)`

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Skeleton className="w-full h-20 rounded-xl" />
        <div className="flex flex-col items-start gap-2">
          <Skeleton className="w-16 h-16 rounded-full -mt-12 ring-4 ring-white" />
          <Skeleton className="w-32 h-5" />
          <Skeleton className="w-40 h-4" />
        </div>
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="w-full h-10 rounded-md" />)}
        </div>
      </div>
    )
  }

  return (
    <aside className="flex flex-col gap-4 p-3">
      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="relative h-20" style={{ background: brandGradient }}>
          <div className="absolute inset-0 opacity-40 pointer-events-none">
            <svg viewBox="0 0 320 80" preserveAspectRatio="none" className="w-full h-full">
              <defs>
                <radialGradient id="profileCardHGrad" cx="80%" cy="0%" r="100%">
                  <stop offset="0%" stopColor="white" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="white" stopOpacity="0" />
                </radialGradient>
              </defs>
              <circle cx="280" cy="0" r="80" fill="url(#profileCardHGrad)" />
            </svg>
          </div>
          <Link href={accountItems.find(i => i.key === 'settings')?.url || '#'} onClick={onLinkClick} className="absolute top-3 right-3 h-7 w-7 inline-flex items-center justify-center rounded-md bg-white/15 text-white hover:bg-white/25 backdrop-blur-sm transition" title="Засах">
            <Pencil className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="px-5 pb-5 -mt-8">
          <div className="relative inline-block">
            <div className="ring-4 ring-white rounded-full inline-block overflow-hidden">
              {customer?.avatar ? (
                <img src={formatImageURL(customer.avatar)} alt="profile" className="w-16 h-16 object-cover rounded-full" />
              ) : (
                <Avatar size={64} variant="marble" name={customer?.email || customer?.firstName || 'shop'} />
              )}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 ring-2 ring-white" title="Идэвхтэй" />
          </div>
          <div className="mt-3 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-base font-semibold leading-tight truncate">{customer?.firstName} {customer?.lastName}</div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">{customer?.email}</div>
            </div>
            {customer?.email && (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                <ShieldCheck className="w-3 h-3" />Verified
              </span>
            )}
          </div>
        </div>
      </div>

      <nav className="rounded-xl border border-border bg-card shadow-sm p-2">
        {visibleItems.map(item => {
          const Icon = ICONS[item.key]
          return (
            <Link key={item.key} href={item.url} onClick={onLinkClick} className={`w-full flex items-center gap-3 px-3 h-10 rounded-md text-sm transition-colors ${item.isActive ? 'bg-foreground/[.04] text-foreground font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}>
              <span className="inline-flex items-center justify-center" style={item.isActive ? { color: '#2B7FFF' } : undefined}>
                <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
              </span>
              <span className="flex-1 text-left">{item.name}</span>
            </Link>
          )
        })}
        {logoutItem && (
          <>
            <div className="my-2 h-px bg-border" />
            <Link href={logoutItem.url} onClick={onLinkClick} className="w-full flex items-center gap-3 px-3 h-10 rounded-md text-sm text-rose-600 hover:bg-rose-50 transition-colors">
              <LogOut className="w-[18px] h-[18px]" strokeWidth={2} />
              <span className="flex-1 text-left">{logoutItem.name}</span>
            </Link>
          </>
        )}
      </nav>
    </aside>
  )
}
